import { ITEM_DEFS, LOOT_TABLES, GOLD_DROP_TABLES } from './ItemDefinitions.js';
import { createItemInstance, EXCEPTIONAL_CHANCES } from './ItemFactory.js';
import WorldItem from './WorldItem.js';

/**
 * LootManager - Handles item drops, world item tracking, auto-pickup, and equipment bonuses.
 * In multiplayer, loot spawns are broadcast so all peers see the same items.
 * Pickups are first-come-first-serve: the collector broadcasts removal to all peers.
 */
export default class LootManager {
  constructor(options = {}) {
    this.world = options.world;
    this.inventory = options.inventory;
    this.audioSystem = options.audioSystem;

    /** @type {Map<string, WorldItem>} */
    this.worldItems = new Map();

    /** Callback when a local item is collected (for dungeon persistence) */
    this.onItemCollected = null;
  }

  /**
   * Drop loot from a defeated enemy based on their loot table.
   * @returns {WorldItem[]} Array of spawned items (for network broadcast)
   */
  /** Drop chance per equipped item slot on LPC NPCs */
  static GEAR_DROP_CHANCE = 0.35;

  dropFromEnemy(enemy, { lootMultiplier = 1 } = {}) {
    const tableName = enemy.combatStats?.lootTable || 'default';
    const table = LOOT_TABLES[tableName] || LOOT_TABLES.default;
    const itemLevel = enemy.combatStats?.level || 1;
    const exceptionalChance = EXCEPTIONAL_CHANCES[tableName] || EXCEPTIONAL_CHANCES.default;
    const spawned = [];

    // Standard loot table rolls
    for (const entry of table) {
      const effectiveChance = Math.min(1, entry.chance * lootMultiplier);
      if (Math.random() <= effectiveChance) {
        const instanceData = createItemInstance(entry.itemId, { itemLevel, exceptionalChance });
        const item = this.spawnItem(entry.itemId, enemy.x, enemy.y, enemy.z, instanceData);
        if (item) spawned.push(item);
      }
    }

    // LPC gear-based drops: each equipped item has a chance to drop
    if (enemy.isLPC && enemy.lpcEquipment) {
      for (const [slot, itemId] of Object.entries(enemy.lpcEquipment)) {
        if (!itemId || !ITEM_DEFS[itemId]) continue;
        if (Math.random() <= LootManager.GEAR_DROP_CHANCE * lootMultiplier) {
          const instanceData = createItemInstance(itemId, { itemLevel, exceptionalChance });
          const item = this.spawnItem(itemId, enemy.x, enemy.y, enemy.z, instanceData);
          if (item) spawned.push(item);
        }
      }
    }

    // Gold drop
    const goldTable = GOLD_DROP_TABLES[tableName] || GOLD_DROP_TABLES.default;
    if (goldTable && this.inventory) {
      const baseGold = goldTable.min + Math.floor(Math.random() * (goldTable.max - goldTable.min + 1));
      this.inventory.addGold(Math.round(baseGold * lootMultiplier));
    }

    return spawned;
  }

  /**
   * Drop loot at a position (used when the NPC was despawned but death was broadcast).
   * Uses default loot table.
   * @returns {WorldItem[]} Array of spawned items
   */
  dropFromEnemyAt(x, y, z) {
    const table = LOOT_TABLES.default;
    if (!table) return [];
    const spawned = [];
    for (const entry of table) {
      if (Math.random() <= entry.chance) {
        const item = this.spawnItem(entry.itemId, x, y, z);
        if (item) spawned.push(item);
      }
    }
    return spawned;
  }

  /**
   * Spawn an item in the world with a random offset.
   * @returns {WorldItem|null}
   */
  spawnItem(itemId, x, y, z, instanceData = null) {
    const def = ITEM_DEFS[itemId];
    if (!def) return null;

    // Slight random offset so multiple drops don't overlap
    const ox = (Math.random() - 0.5) * 0.8;
    const oz = (Math.random() - 0.5) * 0.8;

    const item = new WorldItem({
      itemId, def,
      x: x + ox,
      y: y,
      z: z + oz,
      instanceData
    });

    // Create visual
    item.createVisual(this.world);

    this.worldItems.set(item.id, item);
    return item;
  }

  /**
   * Spawn an item with a pre-determined ID and exact position (from network broadcast).
   * Used by followers to recreate the same items that authority spawned.
   * @param {string} netId - The exact item ID from the broadcaster
   * @param {string} itemId - Item definition ID
   * @param {number} x
   * @param {number} y
   * @param {number} z
   * @returns {WorldItem|null}
   */
  spawnNetworkItem(netId, itemId, x, y, z, quantity = 1, instanceData = null) {
    const def = ITEM_DEFS[itemId];
    if (!def) return null;

    // Don't spawn duplicates
    if (this.worldItems.has(netId)) return null;

    const item = new WorldItem({
      itemId, def,
      x, y, z, quantity,
      instanceData
    });
    // Override the random ID with the network ID
    item.id = netId;

    item.createVisual(this.world);
    this.worldItems.set(item.id, item);
    return item;
  }

  /**
   * Called every frame. Updates hover animation and checks auto-pickup.
   */
  update(deltaTime, player) {
    if (!player || player.combatStats?.isDead) return;

    for (const [id, item] of this.worldItems) {
      if (item.collected) continue;

      item.update(deltaTime);

      // Skip if still immune (just dropped by player)
      if (item._pickupImmunity > 0) continue;

      // Check pickup distance (include Y so items on different floors aren't collected)
      const dx = item.x - player.x;
      const dy = item.baseY - player.y;
      const dz = item.z - player.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

      if (dist <= WorldItem.PICKUP_RANGE) {
        this._collectItem(item, player);
      }
    }
  }

  /**
   * Collect an item and add to inventory.
   * In multiplayer, broadcasts pickup so all peers remove the item.
   * @private
   */
  _collectItem(item, player) {
    const added = this.inventory.addItem(item.itemId, item.quantity, item.def, item.instanceData);
    if (!added) return; // inventory full

    item.collected = true;

    // Pickup sound (type-specific)
    if (this.audioSystem) {
      this.audioSystem.playPickupSound(item.def?.type || 'consumable');
    }

    // Remove visual
    if (item.impostor) {
      this.world.removeImpostor(item.id);
    }

    this.worldItems.delete(item.id);

    // Notify dungeon persistence of local pickup
    if (this.onItemCollected) this.onItemCollected(item.id);

    // Recalculate equipment bonuses
    this._applyEquipmentBonuses(player);

    // Broadcast pickup to all peers (first-come-first-serve)
    const nm = window.game?.networkManager;
    if (nm?.isConnected) {
      nm.broadcastAction({
        type: 'loot-pickup',
        netId: item.id
      });
    }
  }

  /**
   * Handle loot-related network actions.
   * @param {Object} data - Action data with type 'loot-pickup' or 'loot-spawn'
   */
  handleNetworkAction(data) {
    switch (data.type) {
      case 'loot-pickup':
        this._onRemotePickup(data);
        break;
      case 'loot-spawn':
        this._onRemoteLootSpawn(data);
        break;
    }
  }

  /**
   * Remote peer picked up an item — remove it locally.
   * @private
   */
  _onRemotePickup(data) {
    const item = this.worldItems.get(data.netId);
    if (!item || item.collected) return;

    item.collected = true;
    if (item.impostor) {
      this.world.removeImpostor(item.id);
    }
    this.worldItems.delete(item.id);
  }

  /**
   * Remote peer spawned loot items — create them locally.
   * Used for chest opens and NPC deaths when we're not the authority/opener.
   * @private
   */
  _onRemoteLootSpawn(data) {
    if (!data.items || !Array.isArray(data.items)) return;
    for (const loot of data.items) {
      this.spawnNetworkItem(loot.id, loot.itemId, loot.x, loot.y, loot.z, loot.quantity, loot.instanceData || null);
    }
  }

  /**
   * Recalculate equipment bonuses on player's CombatStats.
   */
  _applyEquipmentBonuses(player) {
    if (!player.combatStats || !this.inventory) return;
    const bonuses = this.inventory.getEquipmentBonuses();
    player.combatStats.bonusAttack = bonuses.bonusAtk;
    player.combatStats.bonusDef = bonuses.bonusDef;
    player.combatStats.bonusStr = bonuses.bonusStr || 0;
    player.combatStats.bonusDex = bonuses.bonusDex || 0;
    player.combatStats.bonusInt = bonuses.bonusInt || 0;
    player.combatStats.bonusCon = bonuses.bonusCon || 0;
    player.combatStats.bonusWis = bonuses.bonusWis || 0;
    player.combatStats.bonusEnd = bonuses.bonusEnd || 0;
    // Recompute derived stats from progression + gear bonuses
    const prog = window.game?.progression;
    if (prog) prog.applyTo(player.combatStats);
  }

  /**
   * Test if a position has valid ground and isn't blocked by solid cuboids.
   * @returns {number|null} ground Y if valid, null if blocked/no ground
   */
  _testDropSpot(x, z, playerY, groundDetector, collisionSystem) {
    const itemSize = 0.3;
    const maxYDrop = 2.0;
    if (!groundDetector) return playerY; // no detector, assume player Y is fine

    const groundY = groundDetector.findGroundHeight(x, z, itemSize, itemSize, playerY + 2, 10);
    if (groundY === null || Math.abs(groundY - playerY) > maxYDrop) return null;

    if (collisionSystem) {
      const testBox = {
        minX: x - itemSize / 2, minY: groundY + 0.01,
        minZ: z - itemSize / 2, maxX: x + itemSize / 2,
        maxY: groundY + 0.5,     maxZ: z + itemSize / 2
      };
      const candidates = collisionSystem.queryCuboids(testBox);
      for (const c of candidates) {
        if (c.hasNoCollision && c.hasNoCollision()) continue;
        // Precise AABB test — queryCuboids returns broad-phase candidates
        const cMaxX = c.x + c.width;
        const cMaxY = c.y + c.height;
        const cMaxZ = c.z + c.depth;
        if (testBox.minX < cMaxX && testBox.maxX > c.x &&
            testBox.minY < cMaxY && testBox.maxY > c.y &&
            testBox.minZ < cMaxZ && testBox.maxZ > c.z) {
          return null; // Truly blocked
        }
      }
    }
    return groundY;
  }

  /**
   * Find a valid drop position in front of the player on solid ground.
   * Prefers positions outside pickup range; falls back to closer ones.
   * @returns {{x: number, y: number, z: number}}
   */
  _findDropPosition(player) {
    const groundDetector = window.game?.groundDetector;
    const collisionSystem = window.game?.collisionSystem;
    const angleRad = (player.facingAngle || 0) * (Math.PI / 180);
    const cosA = Math.cos(angleRad);
    const sinA = Math.sin(angleRad);

    // Try out-of-range distances first, then closer fallbacks
    const distances = [2.0, 1.8, 1.5, 1.0, 0.6];
    for (const dist of distances) {
      const tx = player.x + cosA * dist;
      const tz = player.z + sinA * dist;
      const groundY = this._testDropSpot(tx, tz, player.y, groundDetector, collisionSystem);
      if (groundY !== null) {
        return { x: tx, y: groundY, z: tz };
      }
    }

    // Fallback: drop at player's feet
    return { x: player.x, y: player.y, z: player.z };
  }

  /**
   * Spawn a WorldItem at an exact position (no random offset).
   * Used for player-dropped items where the position was pre-validated.
   */
  _spawnItemExact(itemId, x, y, z, quantity = 1) {
    const def = ITEM_DEFS[itemId];
    if (!def) return null;
    const item = new WorldItem({ itemId, def, x, y, z, quantity });
    item.createVisual(this.world);
    this.worldItems.set(item.id, item);
    return item;
  }

  /**
   * Drop an item in front of the player on valid ground.
   * Broadcasts loot-spawn to network peers.
   */
  dropItemAtPlayer(itemId, quantity, player) {
    if (!player) return;
    const dropPos = this._findDropPosition(player);
    const item = this._spawnItemExact(itemId, dropPos.x, dropPos.y, dropPos.z, quantity);
    if (item) {
      item._pickupImmunity = 1.5;
      const nm = window.game?.networkManager;
      if (nm?.isConnected) {
        nm.broadcastAction({
          type: 'loot-spawn',
          items: [{ id: item.id, itemId: item.itemId, x: item.x, y: item.y, z: item.z, quantity: item.quantity }]
        });
      }
    }
  }

  /**
   * Remove all world items (e.g. on level transition).
   */
  clearAll() {
    for (const [id, item] of this.worldItems) {
      if (item.impostor) {
        this.world.removeImpostor(id);
      }
    }
    this.worldItems.clear();
  }
}
